// Extract block-level plain text from markdown using the same AST segmentation
// as ReactMarkdown + rehypeBlockIndexer (top-level elements only).
//
// This ensures translation block indices line up exactly with the
// data-block-index attributes used by TrackableBlock.

import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import remarkRehype from 'remark-rehype';
import rehypeKatex from 'rehype-katex';
import { getRenderableContent } from './normalizeArticleContent';

let processor;

function getProcessor() {
  if (!processor) {
    processor = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkBreaks)
      .use(remarkMath)
      .use(remarkRehype)
      .use(rehypeKatex, { strict: false });
  }
  return processor;
}

function extractTextFromNode(node) {
  if (!node) return '';

  if (node.type === 'text') {
    return node.value || '';
  }

  if (!node.children || node.children.length === 0) {
    // For image-only blocks, fall back to alt text so translations have context
    if (node.tagName === 'img' && node.properties && typeof node.properties.alt === 'string') {
      return node.properties.alt;
    }
    // HR elements have no text content for translation/audio - return empty string
    // They still get a block index for selection/deletion purposes
    if (node.tagName === 'hr') {
      return '';
    }
    return '';
  }

  return node.children.map(extractTextFromNode).join('');
}

/**
 * Core implementation for extracting block texts from markdown.
 * @param {string} markdown - The markdown content
 * @param {boolean} splitListItems - If true, list items become separate blocks (V2 behavior)
 * @returns {Array<string>} Array where index === block index, value is plain text
 */
function extractMarkdownBlocksCore(markdown, splitListItems) {
  const clean = getRenderableContent(markdown || '');
  if (!clean) return [];

  const proc = getProcessor();
  const tree = proc.runSync(proc.parse(clean));

  const blocks = [];
  if (!tree || !Array.isArray(tree.children)) return blocks;

  let blockIndex = 0;
  for (const node of tree.children) {
    if (node.type === 'element') {
      // Check if this is a top-level list (ul or ol) and we should split
      if (splitListItems && (node.tagName === 'ul' || node.tagName === 'ol')) {
        // Split list items into individual blocks
        const listItems = node.children.filter(child =>
          child.type === 'element' && child.tagName === 'li'
        );

        for (const li of listItems) {
          const text = extractTextFromNode(li).trim();
          blocks[blockIndex] = text;
          blockIndex += 1;
        }
      } else {
        // Non-list elements or V1: extract text normally
        const text = extractTextFromNode(node).trim();
        blocks[blockIndex] = text;
        blockIndex += 1;
      }
    }
  }

  return blocks;
}

/**
 * Extract block texts aligned with data-block-index produced by rehypeBlockIndexer.
 * Version 1: Original behavior - lists as single blocks.
 *
 * Returns an array where:
 *   - index === block index (0, 1, 2, ...)
 *   - value is the plain text for that top-level block
 */
export function extractMarkdownBlocks(markdown) {
  return extractMarkdownBlocksCore(markdown, false);
}

/**
 * Extract block texts aligned with rehypeBlockIndexerV2.
 * Version 2: List items are split into individual blocks.
 *
 * Returns an array where:
 *   - index === block index (0, 1, 2, ...)
 *   - value is the plain text for that block
 */
export function extractMarkdownBlocksV2(markdown) {
  return extractMarkdownBlocksCore(markdown, true);
}

/**
 * Get the appropriate block extractor based on version
 * @param {number} version - Block version (1 or 2)
 * @returns {Function} Extraction function
 */
export function getExtractMarkdownBlocks(version) {
  return version === 2 ? extractMarkdownBlocksV2 : extractMarkdownBlocks;
}

